import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Landmark, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
import { payoutsApi, type PayoutStatus } from '@/api/payoutsApi';

/** Shown on every partner page until settlements are active. */
export function PayoutSetupBanner() {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<PayoutStatus | null>(null);

  useEffect(() => {
    let live = true;
    payoutsApi
      .getMine()
      .then(({ data }) => live && setStatus(data.payout.status))
      .catch(() => live && setStatus(null));
    return () => {
      live = false;
    };
  }, [pathname]);

  if (!status || status === 'active' || pathname.startsWith('/partner/payouts')) return null;

  const copy = {
    not_started: {
      icon: Landmark,
      title: 'Set up payment settlement',
      body: 'Add the UPI id that should receive your booking payments. Takes a minute.',
      cta: 'Set up now',
      tone: 'border-primary/30 bg-primary/5 text-foreground',
    },
    pending_verification: {
      icon: Clock,
      title: 'Settlement details are being verified',
      body: 'Usually done within a day. Nothing more is needed from you.',
      cta: 'View',
      tone: 'border-border bg-muted/40 text-foreground',
    },
    rejected: {
      icon: AlertTriangle,
      title: "We couldn't verify your settlement details",
      body: 'Please check them and submit again so payouts are not delayed.',
      cta: 'Fix now',
      tone: 'border-amber-300 bg-amber-50 text-amber-950',
    },
  }[status];

  const Icon = copy.icon;
  return (
    <Link
      to="/partner/payouts"
      className={`mb-6 flex items-center gap-3 rounded-xl border p-4 text-sm transition hover:shadow-sm ${copy.tone}`}
    >
      <Icon className="size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{copy.title}</p>
        <p className="truncate opacity-80">{copy.body}</p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 font-medium">
        {copy.cta} <ArrowRight className="size-4" />
      </span>
    </Link>
  );
}
